import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext, FunctionElement } from '../../element.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility, resolveStylePath } from '../feature-style.model';
import { TANGENT_DEFAULT_PARAMS, type TangentParams } from './tangent.model';

/**
 * Creates a tangent line to a function at a specified x location.
 *
 * Subparts:
 * - `touchPoint`: The point where the tangent touches the curve (draggable glider)
 * - `tangentLine`: The tangent line itself
 */
export class TangentCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'tangent';
  override readonly computedValueKeys = ['slope', 'x', 'y', 'yIntercept'] as const;

  create(
    id: string,
    def: FeatureElementDef<'tangent'>,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as TangentParams | undefined;
    if (!params?.of) {
      throw new Error('Tangent feature requires params.of to specify the parent function');
    }
    const parentFn: FunctionElement = this.getParentFunction(params.of, context);
    const objects: GeometryElement[] = [];

    const curveElement = parentFn.objects.find((obj) => obj.elType === 'curve');
    if (!curveElement) {
      throw new Error('Tangent feature requires parent function to have a curve element');
    }

    // Create glider using factory (label is now part of style)
    const touchPointStyle = resolveFeatureStyle('point', this.featureType, context, display, ['touchPoint']);
    const touchPointVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['touchPoint']);
    const touchPointFixed = this.resolveFixed(context, display, ['touchPoint']);

    const initialX = params.x ?? TANGENT_DEFAULT_PARAMS.x;
    const initialY = parentFn.evaluate(initialX);

    const gliderResult = elementFactory.glider({
      coords: [initialX, initialY],
      slideObject: curveElement,
      name: 'P',
      style: { ...touchPointStyle, visible: touchPointVisible, fixed: touchPointFixed },
      onDrag: (x) => {
        context.onParamChange?.(id, { x });
      },
    });
    objects.push(gliderResult.glider);
    if (gliderResult.label) {
      objects.push(gliderResult.label);
    }

    // Typed glider for getParams/setParams
    const glider = gliderResult.glider as unknown as {
      X: () => number;
      moveTo: (coords: [number, number]) => void;
    };

    // Create tangent line using JSXGraph's native tangent (no factory method for this)
    const tangentLineStyle = resolveFeatureStyle('line', this.featureType, context, display, ['tangentLine']);
    const tangentLineVisible = resolveFeatureVisibility(this.featureType, featureTheme, display, ['tangentLine']);

    const tangentLine = board.create('tangent', [gliderResult.glider], {
      strokeColor: tangentLineStyle.color ? context.themeService.resolveColorToken(tangentLineStyle.color) : undefined,
      strokeWidth: tangentLineStyle.strokeWidth ?? 2,
      dash: tangentLineStyle.dash ?? 0,
      visible: tangentLineVisible,
      straightFirst: true,
      straightLast: true,
      withLabel: false,
    }) as GeometryElement;
    objects.push(tangentLine);

    return {
      id,
      type: 'tangent',
      objects,
      getParams: () => ({ x: glider.X() }),
      setParams: (newParams) => {
        if (newParams['x'] !== undefined) {
          const y = parentFn.evaluate(newParams['x']);
          glider.moveTo([newParams['x'], y]);
          board.update();
        }
      },
      getComputedValues: () => ({
        slope: () => parentFn.getDerivative()(glider.X()),
        x: () => glider.X(),
        y: () => parentFn.evaluate(glider.X()),
        yIntercept: () => {
          const x = glider.X();
          const y = parentFn.evaluate(x);
          const slope = parentFn.getDerivative()(x);
          return y - slope * x;
        },
      }),
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }

  private resolveFixed(
    context: ElementContext,
    display: ElementDisplayConfig | undefined,
    path: string[] = [],
  ): boolean {
    const { featureTheme } = context;
    const featureNode = featureTheme[this.featureType];
    const featureResolved = resolveStylePath(featureNode, path);
    const displayResolved = resolveStylePath(display, path);

    if (displayResolved.point?.fixed !== undefined) {
      return displayResolved.point.fixed;
    }
    if (featureResolved.point?.fixed !== undefined) {
      return featureResolved.point.fixed;
    }
    return false;
  }
}
